import React from 'react';
import L from 'leaflet';
import { Marker, Polygon, useMap } from 'react-leaflet';
import { useMapContext } from '../context/map-context';
import { useStore } from '../../../../store';

interface ICustomPolygonProps {
  title:string
  zoneCoords: [number, number][]; // Explicitly typed as a tuple array
}

export const CustomPolygon: React.FC<ICustomPolygonProps> = ({ title, zoneCoords }) => {
  const map = useMap();
  const { configurationValues, imageAngle, rangePerDegree } = useMapContext();
  const { mapOrigin } = useStore();

  const rangeToCoordinates = (rangeX: number, rangeY: number): [number, number] | null => {
    if (!mapOrigin.data.latitude || !mapOrigin.data.longitude || !rangePerDegree) return null;

    const latitude = Number(mapOrigin.data.latitude) + rangeY / rangePerDegree.lat;
    const longitude = Number(mapOrigin.data.longitude) + rangeX / rangePerDegree.lon;

    return [latitude, longitude];
  };

  const calculateCentroid = (coords: ([number, number] | null)[]): [number, number] | null => {
    const validCoords = coords.filter((coord): coord is [number, number] => coord !== null);
    if (validCoords.length === 0) return null;

    const centroid = validCoords.reduce(
      (acc, [lat, lng]) => {
        acc[0] += lat;
        acc[1] += lng;
        return acc;
      },
      [0, 0] as [number, number],
    );

    return [centroid[0] / validCoords.length, centroid[1] / validCoords.length];
  };

  if (!mapOrigin.data.latitude || !mapOrigin.data.longitude) return null;

  function rotatePoint(
    point: [number, number],
    center: [number, number],
    angle: number,
  ) {
    const angleRad = (angle * Math.PI) / 180;

    const pointProjected = map.project(L.latLng(point));
    const centerProjected = map.project(L.latLng(center));

    const deltaX = pointProjected.x - centerProjected.x;
    const deltaY = pointProjected.y - centerProjected.y;

    const rotatedX = deltaX * Math.cos(angleRad) - deltaY * Math.sin(angleRad);
    const rotatedY = deltaX * Math.sin(angleRad) + deltaY * Math.cos(angleRad);

    const rotatedPointProjected = L.point(
      centerProjected.x + rotatedX,
      centerProjected.y + rotatedY,
    );

    return map.unproject(rotatedPointProjected);
  }

  const transformedCoords:[number, number] | null = zoneCoords.map(([x,
    y]) => rangeToCoordinates(x, y)).filter(Boolean).map((coord) => {
    if (!coord) return null;
    const points = rotatePoint(coord, [Number(mapOrigin.data.latitude),
      Number(mapOrigin.data.longitude)], imageAngle);
    return [points.lat, points.lng];
  });

  const centroid = calculateCentroid(transformedCoords);

  const label = L.divIcon({
    html: configurationValues?.visibility?.zoneLabels ? title : '',
    className: 'custom-label-icon custom-zone-opacity custom-zoneLabel-visibility',
  });

  return (
    <Polygon
      className="custom-zone-opacity custom-zone-visibility"
      color="#0F6AFD12"
      positions={transformedCoords as [number, number][]}
      pathOptions={{
        color: '#5A3AEB',
        dashArray: '15 15',
      }}
    >
      {centroid && (
        <Marker position={centroid} icon={label} />
      )}
    </Polygon>
  );
};
